// ForgotPassword.tsx — public page that kicks off the password-reset flow.
// On submit we always show the same neutral confirmation (the backend request
// endpoint is enumeration-safe), so we never reveal whether an account exists.

import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button, Field, Input } from '@/foundation/primitives';
import { requestPasswordReset } from '@/auth/passwordReset';
import { AuthShell, authLink } from './AuthShell';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await requestPasswordReset(email.trim());
    } catch {
      // Enumeration-safe: never surface request errors to the user. Show the
      // same neutral state regardless of outcome.
    } finally {
      setSubmitting(false);
      setSent(true);
    }
  }

  const backFooter = (
    <Link to="/login" style={authLink}>
      Back to sign in
    </Link>
  );

  if (sent) {
    return (
      <AuthShell
        title="Check your email."
        deck="If that account exists, a reset link has been sent."
        footer={backFooter}
      >
        <p className="body2" style={{ margin: 0, color: 'var(--text-secondary)' }}>
          The link stays valid for a short while — request another from this page if it expires.
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Forgot your password?"
      deck="Enter your email and we'll send you a reset link."
      footer={backFooter}
    >
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="Email">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            placeholder="you@pharmacy.com"
          />
        </Field>

        <Button
          disabled={submitting || !email}
          style={{ width: '100%', justifyContent: 'center', height: 44 }}
        >
          {submitting ? 'Sending…' : 'Send reset link'}
        </Button>
      </form>
    </AuthShell>
  );
}
